<template>
  <div class="account-settings-page">
    <AccountPageTopBar :title="$t('accountSettings.title')" @back="goBack" />

    <main class="account-settings-page__content">
      <section class="settings-card">
        <header class="settings-card__header">
          <span><img src="@/assets/img/profile/account/agreements.svg" alt=""></span>
          <h2>{{ $t('accountSettings.agreementsPolicies') }}</h2>
        </header>
        <div class="settings-card__body">
          <p v-if="displayAgreements.length === 0" class="settings-card__empty">
            {{ $t('accountSettings.noAgreements') }}
          </p>
          <button
            v-for="(agreement, index) in displayAgreements"
            :key="agreement.id || index"
            class="settings-card__row"
            type="button"
            @click="openAgreement(agreement, index)"
          >
            <span>{{ agreementTitle(agreement, index) }}</span>
            <Icon name="arrow" />
          </button>
        </div>
      </section>

      <section class="settings-card">
        <header class="settings-card__header">
          <span><img src="@/assets/img/profile/account/about.svg" alt=""></span>
          <h2>{{ $t('accountSettings.aboutUs') }}</h2>
        </header>
        <div class="settings-card__body">
          <button class="settings-card__row" type="button" @click="$router.push('/profile/about-us')">
            <span>{{ $t('accountSettings.aboutUs') }}</span>
            <Icon name="arrow" />
          </button>
        </div>
      </section>

      <section class="settings-card">
        <header class="settings-card__header">
          <span><img src="@/assets/img/profile/account/security.svg" alt=""></span>
          <h2>{{ $t('accountSettings.accountSecurity') }}</h2>
        </header>
        <div class="settings-card__body">
          <button class="settings-card__row" type="button" @click="goDeleteAccount">
            <span>{{ $t('accountSettings.deleteAccount') }}</span>
            <Icon name="arrow" />
          </button>
        </div>
      </section>
    </main>

    <Popup
      v-model="agreementVisible"
      class="agreement-preview"
      :close-on-click-overlay="false"
    >
      <header class="agreement-preview__header">
        <h2>{{ selectedAgreementTitle }}</h2>
        <button type="button" :aria-label="$t('close')" @click="agreementVisible = false">
          <Icon name="cross" />
        </button>
      </header>
      <iframe
        v-if="agreementVisible"
        :key="agreementPreviewKey"
        class="agreement-preview__content"
        :srcdoc="selectedAgreementHtml"
        sandbox=""
        frameborder="0"
        :title="selectedAgreementTitle"
      ></iframe>
    </Popup>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Dialog, Icon, Popup } from 'vant';
import { getAgreements } from '@/api/common';
import AccountPageTopBar from '@/components/AccountPageTopBar/index.vue';
import routerBack from '@/mixins/routerBack';

const AGREEMENT_TITLE_I18N_KEYS = {
  'privacy policy': 'accountSettings.privacyPolicy',
  'device rental agreement': 'accountSettings.deviceRentalAgreement',
  'account delete agreement': 'accountSettings.accountDeleteAgreement',
  'account deletion agreement': 'accountSettings.accountDeleteAgreement',
  'personal data processing consent': 'accountSettings.dataProcessingConsent',
  'data processing consent': 'accountSettings.dataProcessingConsent',
  'user registration agreement': 'accountSettings.userRegistrationAgreement',
};

const AGREEMENT_TITLE_ORDER = {
  'privacy policy': 0,
  'device rental agreement': 1,
  'account delete agreement': 2,
  'account deletion agreement': 2,
  'personal data processing consent': 3,
  'data processing consent': 3,
  'user registration agreement': 4,
};

const normalizeAgreementTitle = title => String(title || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .replace(/[.。]+$/, '');

export default {
  name: 'ProfileSettings',
  mixins: [routerBack],
  components: {
    AccountPageTopBar,
    Icon,
    Popup,
  },
  data() {
    return {
      agreements: [],
      agreementVisible: false,
      agreementPreviewKey: 0,
      selectedAgreementTitle: '',
      selectedAgreementHtml: '',
    };
  },
  computed: {
    ...mapState('user', ['session']),
    isLogin() {
      return !!this.session?.accessToken;
    },
    displayAgreements() {
      return this.agreements
        .map((agreement, index) => ({
          agreement,
          index,
          order: AGREEMENT_TITLE_ORDER[normalizeAgreementTitle(agreement?.title)] ?? 100,
        }))
        .sort((left, right) => left.order - right.order || left.index - right.index)
        .map(item => item.agreement);
    },
  },
  mounted() {
    this.loadAgreements();
  },
  methods: {
    async loadAgreements() {
      try {
        const res = await getAgreements(
          { sceneCode: 'ALL' },
          { _showLoading: false }
        );
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        this.agreements = Array.isArray(res?.data?.agreements) ? res.data.agreements : [];
      } catch (error) {
        this.agreements = [];
        this.$toast(error?.message || this.$t('accountSettings.loadFailed'));
      }
    },
    agreementTitle(agreement, index) {
      const rawTitle = String(agreement?.title || '').trim();
      const normalizedTitle = normalizeAgreementTitle(rawTitle);
      const titleKey = AGREEMENT_TITLE_I18N_KEYS[normalizedTitle];
      if (titleKey) return this.$t(titleKey);
      if (rawTitle) return rawTitle;
      const fallbackKeys = [
        'accountSettings.userRegistrationAgreement',
        'accountSettings.privacyPolicy',
        'accountSettings.dataProcessingConsent',
        'accountSettings.deviceRentalAgreement',
        'accountSettings.accountDeleteAgreement',
      ];
      return this.$t(fallbackKeys[index] || fallbackKeys[4]);
    },
    decodeAgreementContent(content) {
      const value = String(content || '');
      if (!value) return '';
      try {
        const binary = window.atob(value);
        const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
        if (window.TextDecoder) return new TextDecoder('utf-8').decode(bytes);
        return decodeURIComponent(Array.from(bytes, byte => `%${byte.toString(16).padStart(2, '0')}`).join(''));
      } catch {
        return value;
      }
    },
    buildAgreementHtml(content) {
      return `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;color:#111827;font-family:Arial,sans-serif;font-size:15px;line-height:1.65;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%;border-collapse:collapse}</style></head><body>${content}</body></html>`;
    },
    openAgreement(agreement, index) {
      this.selectedAgreementTitle = this.agreementTitle(agreement, index);
      this.selectedAgreementHtml = this.buildAgreementHtml(this.decodeAgreementContent(agreement?.content));
      this.agreementPreviewKey += 1;
      this.agreementVisible = true;
    },
    goDeleteAccount() {
      if (this.isLogin) {
        this.$router.push('/profile/delete-account');
        return;
      }
      Dialog.confirm({
        title: this.$t('myPage.loginRequiredTitle'),
        message: this.$t('myPage.loginRequiredContent'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      })
        .then(() => {
          this.$router.push({
            path: '/login',
            query: { redirect: '/profile/delete-account' },
          });
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="less" scoped>
.account-settings-page {
  min-height: 100vh;
  background: #f4f8ff;
  color: #070d3f;
}

.account-settings-page__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 112px 16px calc(32px + env(safe-area-inset-bottom));
}

.settings-card {
  overflow: hidden;
  padding: 16px;
  border: 2px solid #e5eaf3;
  border-radius: 20px;
  background: #ffffff;
}

.settings-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px 16px;
  border-bottom: 2px solid #e5eaf3;

  > span {
    display: flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #eaf3ff;
    color: #0b45ff;

    img {
      width: 28px;
      height: 28px;
    }
  }

  h2 {
    margin: 0;
    font-size: 30px;
    font-weight: 500;
    line-height: 40px;
  }
}

.settings-card__body {
  .settings-card__row + .settings-card__row {
    border-top: 2px solid #e5eaf3;
  }
}

.settings-card__row {
  display: flex;
  width: 100%;
  min-height: 84px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 16px;
  border: 0;
  background: transparent;
  color: #070d3f;
  font-size: 26px;
  font-weight: 400;
  line-height: 36px;
  text-align: left;
  box-sizing: border-box;

  .van-icon {
    flex-shrink: 0;
    color: #6b7280;
    font-size: 32px;
  }
}

.settings-card__empty {
  margin: 0;
  padding: 24px 16px;
  color: #6b7280;
  font-size: 24px;
  line-height: 32px;
}

::v-deep .agreement-preview {
  display: flex;
  width: calc(100% - 64px);
  max-width: 686px;
  height: calc(100% - 64px);
  max-height: 1200px;
  flex-direction: column;
  overflow: hidden;
  border-radius: 32px;
  background: #ffffff;
}

.agreement-preview__header {
  display: flex;
  min-height: 96px;
  align-items: center;
  padding: 16px 16px 16px 32px;
  border-bottom: 2px solid #e5eaf3;
  box-sizing: border-box;

  h2 {
    min-width: 0;
    margin: 0;
    flex: 1;
    overflow: hidden;
    font-size: 36px;
    font-weight: 600;
    line-height: 52px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: flex;
    width: 80px;
    height: 80px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: #070d3f;
    font-size: 40px;
  }
}

.agreement-preview__content {
  width: 100%;
  min-height: 0;
  flex: 1;
}
</style>
